import { useMemo, useState } from 'react';
import type { Member, Role, RoleRequest } from '@accounts/domain';
import { usePresence } from '@accounts/session';
import { ActionMenu, ChoiceChip, DataTable, EmptyState, LastUpdate, ListRow, Status, type Moment } from '@beyond-js/ui/react';
import { Panel, Unsent, useTableAddress } from '@accounts/ui';
import type { Requests } from './requests';

const ranks: Record<Role, number> = { owner: 0, admin: 1, developer: 2, viewer: 3 };
const time = (value?: string) => (value ? Date.parse(value) : 0);

/**
 * Who is in an organization (ACC-15, ACC-21, ARR-13), as the family's table (DQ-12): searched, sorted
 * by name, role, when they joined and, for owners and administrators, when they last used Beyond, its
 * state kept in the address. Each member is named and, for owners and administrators, shown with their
 * verified address. A member's actions are in their row's menu, offered by role and named with the
 * member: an administrator is offered nothing on an owner's row, and only an owner hands out ownership.
 * A developer or viewer reads, after the table, who invites people, changes roles and removes members:
 * nothing is refused there, so it is the region's last quiet line, never `Unavailable`'s refusal (D06, D130).
 * The table's search and, for owners and administrators, the Show chip (current members, or removed ones too) are the region's tools,
 * in the Members heading's action area (L8), as the invitations' filter is in theirs: the team holds both
 * regions, so each list's tools stay with its own heading.
 * Every member can leave from their own row. Open role requests come first as rows with Grant and
 * Decline; when Beyond Accounts did not answer their read they say "Not available right now" in their
 * place, never nothing, and requests kept from an earlier read say when that was (D163). Removed members
 * are hidden unless asked for.
 */
export function Members({
	members: read,
	updated,
	requests,
	role,
	all,
	unsent,
	onAll,
	onChange,
	onRemove,
	onLeave,
	onDecline
}: {
	/** Undefined while the organization is first read: the region's heading is drawn and its table waits */
	members?: Member[];
	/** When the members shown were read, while a later read does not answer (D163); null when not known */
	updated?: Moment | null;
	/** The open role requests, for the owners and administrators who decide on them */
	requests: Requests;
	role?: Role;
	all: boolean;
	unsent?: string;
	onAll: (all: boolean) => void;
	onChange: (member: Member, role?: Role) => void;
	onRemove: (member: Member) => void;
	onLeave: () => void;
	onDecline: (request: RoleRequest) => void;
}) {
	const { text, date, identity, language } = usePresence();
	const [state, onState] = useTableAddress('members');
	const members = read ?? [];
	const me = identity?.account.id;
	const steward = role === 'owner' || role === 'admin';
	// Where the table draws its search: the heading's action area
	const [tools, setTools] = useState<HTMLElement | null>(null);

	/** What this person may do on a row, and nothing more (ACC-15) */
	function actions(member: Member) {
		const owners = members.filter(entry => entry.state === 'active' && entry.role === 'owner').length;
		if (member.account === me) {
			// Lowering yourself is offered with its warning; the last owner keeps the role (ARR-10)
			const lowerable = steward && !(member.role === 'owner' && owners <= 1);
			return [...(lowerable ? [{ label: text('members.change'), onSelect: () => onChange(member) }] : []), { label: text('leave.action'), onSelect: onLeave, tone: 'danger' as const }];
		}
		if (!steward || member.state !== 'active' || (member.role === 'owner' && role !== 'owner')) return [];
		return [
			{ label: text('members.change'), onSelect: () => onChange(member) },
			{ label: text('members.remove'), onSelect: () => onRemove(member), tone: 'danger' as const }
		];
	}

	// A cell's React content is drawn with its row, so the rows are handed again whenever what a cell
	// shows besides the member changes (a change left unsent, the viewer's role)
	const rows = useMemo(() => members.map(member => ({ ...member })), [members, unsent, role, me]);
	const name = (member: Member) => member.member ?? text('members.unnamed');
	const columns = [
		{
			key: 'name',
			label: text('members.sort.name'),
			primary: true,
			sort: true,
			value: (member: Member) => `${name(member)}${member.account === me ? ` · ${text('members.you')}` : ''}`
		},
		// The address tells two similar names apart (ARR-13); only owners and administrators see it
		...(steward ? [{ key: 'email', label: text('members.column.address'), value: (member: Member) => member.email ?? '' }] : []),
		{
			key: 'role',
			label: text('members.sort.role'),
			sort: (a: Member, b: Member) => ranks[a.role] - ranks[b.role],
			value: (member: Member) => (member.state === 'removed' ? text('members.removed') : text(`role.${member.role}`)),
			render: (member: Member) =>
				member.state === 'removed' ? <Status state="stopped" label={text('members.removed')} /> : <span>{text(`role.${member.role}`)}</span>
		},
		{
			key: 'joined',
			label: text('members.sort.joined'),
			sort: (a: Member, b: Member) => time(a.joined) - time(b.joined),
			value: (member: Member) => (member.joined ? date(member.joined) : '')
		},
		...(steward
			? [
					{
						key: 'active',
						label: text('members.sort.active'),
						sort: (a: Member, b: Member) => time(a.active) - time(b.active),
						value: (member: Member) => (member.active ? date(member.active) : text('members.active.never'))
					}
				]
			: []),
		{
			key: 'actions',
			menu: true,
			label: text('members.column.actions'),
			align: 'end' as const,
			value: () => '',
			render: (member: Member) => {
				const items = actions(member);
				return (
					<>
						{items.length ? <ActionMenu label={null} name={text('members.actions', { name: name(member) })} items={items} /> : null}
						<Unsent end shown={unsent === member.account} />
					</>
				);
			}
		}
	];

	return (
		<Panel
			id="members"
			title={text('members.title')}
			// The count is said once it is known, never guessed while the table waits
			description={read ? text('members.count', { count: members.filter(m => m.state === 'active').length }) : undefined}
			waiting={read ? null : { kind: 'table', rows: 3 }}
			updated={read ? updated : undefined}
			actions={
				<div className="flex flex-wrap items-center gap-x-4 gap-y-2">
					<div ref={setTools} />
					{steward ? (
						<ChoiceChip
							label={text('show')}
							value={all ? 'all' : 'current'}
							options={[
								{ value: 'current', label: text('members.show.current') },
								{ value: 'all', label: text('members.show.all') }
							]}
							onChange={value => onAll(value === 'all')}
							labels={ChoiceChip.labels[language]}
						/>
					) : null}
				</div>
			}
		>
			{steward && requests.unavailable && !requests.list ? (
				<div role="group" aria-label={text('members.requests')}>
					<p data-bui-label className="text-small text-ink-muted">
						{text('members.requests')}
					</p>
					<EmptyState kind="outage" frame={false} labels={EmptyState.labels[language]} />
				</div>
			) : null}
			{/* Kept from an earlier read: when that was, unless the whole region says it already */}
			{steward && requests.list?.length && requests.updated !== undefined && updated === undefined ? (
				<LastUpdate time={requests.updated} locale={language} labels={LastUpdate.labels[language]} />
			) : null}
			{steward && requests.list?.length ? (
				<ul className="bui-rows bui-rows-aligned mb-4" data-framed aria-label={text('members.requests')}>
					{requests.list.map(request => (
						<ListRow
							key={request.id}
							title={text('members.request', { name: request.name ?? text('members.unnamed'), role: text(`role.${request.role}`) })}
							meta={request.note ? text('members.request.note', { note: request.note }) : null}
							lead={{ state: 'attention' }}
							actions={[
								{
									label: text('members.request.grant', { role: text(`role.${request.role}`) }),
									onSelect: () => {
										const member = members.find(entry => entry.account === request.account);
										if (member) onChange(member, request.role);
									}
								},
								{ label: text('members.request.decline'), onSelect: () => onDecline(request) }
							]}
						/>
					))}
				</ul>
			) : null}

			<div className="bui-raised">
				<DataTable<Member>
					framed
					// The table's label completes its search's words ("Search members")
					label={text('members.table')}
					columns={columns}
					rows={rows}
					rowKey={member => member.account}
					search
					tools={tools ? 'external' : undefined}
					toolsTarget={tools}
					state={state}
					onChange={onState}
					labels={DataTable.labels[language]}
				/>
			</div>
			{/* Someone who cannot manage the members is told who can, with nothing refused (D06): the region's last
			    quiet line, so it never opens with prose (D130) */}
			{role && !steward ? <p className="mt-3 max-w-reading text-small text-ink-muted">{text('organization.manage.note')}</p> : null}
		</Panel>
	);
}
